import React from "react";
import styles from "./index.module.scss";
import Tags from "../Tags";
import SocialNetworks from "../../../UI/SocialNetworks";
import * as ReactDOMServer from "react-dom/server";
import { renderImageComponent } from "./Image";
import { renderQuoteComponent } from "./Quote";
import { renderMessageComponent } from "./Message";
import { renderExternalLinkComponent } from "./ExternalLink";
import Code from "./Code";
import dynamic from "next/dynamic";
import InfiniteScrollHeader from "./InfiniteScrollHeader";
import { renderEmbedComponent } from "./Embed";
import Banner from "components/Blog/Banner";
import Author from "components/Blog/Author";

const NavigationMenu = dynamic(() => import("./NavigationMenu"), {
  ssr: false,
});

function getNearestIndex(arrayLength) {
  const targetIndex = Math.round(arrayLength * 0.6) - 1;
  return targetIndex;
}

const bannerData = [
  {
    title:
      "Apphud is a reliable and easy-to-integrate in-app subscription infrastructure that helps you grow your app revenue",
    image: "/svgs/icons/build.svg",
    buttonText: "Learn more",
    buttonUrl: "/development",
  },
  {
    title:
      "Apphud is a subscription data platform that reveals your app’s growth points",
    description:
      "Test pricing and paywalls. Automatically win back lapsed customers",
    image: "/svgs/icons/robot.svg",
    buttonText: "Learn more",
    buttonUrl: "/product",
  },
  {
    title:
      "Apphud is a subscription data platform that gives you real-time, accurate data to increase your app revenue",
    description:
      "Analyze valuable revenue metrics. Use integrations to send subscription events to other tools.",
    image: "/svgs/icons/infrastructure.svg",
    buttonText: "Learn more",
    buttonUrl: "/marketing",
  },
  {
    title:
      "Looking for a reliable in-app subscription infrastructure to make product growth decisions? ",
    description:
      "Apphud provides the most accurate revenue data with actionable insights to grow your app.",
    image: "/svgs/icons/measure.png",
    buttonText: "Why Apphud?",
    buttonUrl: "/why-apphud",
  },
];

const getComponent = (props) => {
  const { type, data = {}, id = "" } = props;
  const { level = null } = data;
  switch (type) {
    case "header":
      // eslint-disable-next-line no-case-declarations
      const text = data.text.replace(
        /\`(.*)\`/,
        "<code class='inline-code'>$1</code>"
      );
      if (level === 2) {
        return [
          "div",
          null,
          <InfiniteScrollHeader text={text} id={id} key={id} />,
        ];
      }
      return [`h${level}`, text.trim()];
    case "paragraph":
      return ["p", data.text];
    case "code":
      return ["div", null, <Code value={data.code} key={id} />];
    case "delimiter":
      return ["div", "<hr/>"];
    case "image":
      return [
        "div",
        renderImageComponent({
          src: data.file.url,
          alt: data.caption,
          border: data.withBorder,
          stretched: data.stretched,
          center: data.withBackground,
        }),
      ];
    case "embed":
      return [
        "div",
        renderEmbedComponent({
          caption: data.caption,
          embed: data.embed,
          height: data.height,
          source: data.source,
          width: data.width,
        }),
      ];
    case "quote":
      return [
        "div",
        renderQuoteComponent({
          text: data.text,
          caption: data.caption,
        }),
      ];
    case "warning":
      return [
        "div",
        renderMessageComponent({
          text: data.message,
          type: "warning",
        }),
      ];
    case "list":
      return [
        "ul",
        ReactDOMServer.renderToString(
          data.items.map((item, k) => (
            <li key={k} dangerouslySetInnerHTML={{ __html: item }} />
          ))
        ),
      ];
    case "linkTool":
      return [
        "div",
        renderExternalLinkComponent({
          title: data?.meta?.title,
          link: data.link,
          description: data?.meta?.description,
          image: data.meta?.image?.url,
        }),
      ];
    default:
      return ["div", ""];
  }
};

const getBannerType = (bannerType) => {
  switch (bannerType) {
    case "dev":
      return bannerData[0];
    case "product":
      return bannerData[1];
    case "marketing":
      return bannerData[2];
    case "why_us":
      return bannerData[3];
    default:
      return null;
  }
};

const getUserInfo = (user) => {
  const users = {
    Nataly: {
      title: "Nataly",
      subtitle: "Head of Marketing at Apphud",
      description:
        "7+ years in product marketing. Nataly is responsible for marketing strategy development and execution. Committed adherent of the agile methodology.",
      image: user?.avatar?.original,
    },
    Ren: {
      title: "Ren",
      subtitle: "Co-founder at Apphud",
      description:
        "Ex iOS app and game developer. 11 years in the industry since iOS 3. More than 50 apps are in the background with 4 exits. Entrepreneur and traveler.",
      image: user?.avatar?.original,
    },
    Anton: {
      title: "Anton",
      subtitle: "Head of Product at Apphud",
      description:
        "10+ years in mobile. Anton started his career as a backend developer, last 3 years has been working as a product manager. He created a podcast dedicated to software dev. Successfully launched (and sold) his own apps.",
      image: user?.avatar?.original,
    },
    Jane: {
      title: "Jane",
      subtitle: "Head of Business Development at Apphud",
      description:
        "10+ years of experience in Project Management and Business Development. Jane began her professional journey as a Sales Manager. Over time, she successfully established herself as Product Owner, and BizDev Lead.",
      image: user?.avatar?.original,
    },
    Alex: {
      title: "Alex",
      subtitle: "CTO at Apphud",
      description:
        "Software architect and engineer with 12+ years experience. More than 50 projects are in the background with 5 successful exits.",
      image: user?.avatar?.original,
    },
    Denis: {
      title: "Denis",
      subtitle: "CEO & Co-founder at Apphud",
      description:
        "Serial IT entrepreneur with expertise in mobile app monetization. Former iOS developer and UI/UX designer with 12-year background in the mobile industry, fueled by a passion for innovation.",
      image: user?.avatar?.original,
    },
  };

  return users[user?.first_name] || null;
};

export default function Content({ blocks = [], tags = [], banner, author }) {
  const sections = blocks.filter(
    (el) => el.type === "header" && el.data.level === 2
  );

  let displayIndex = getNearestIndex(blocks.length);
  const bannerElement = getBannerType(banner);

  return (
    <div className={styles.container}>
      <div className={styles.sidebar}>
        <NavigationMenu sections={sections} />
      </div>
      <div className={styles.content}>
        {blocks.map((block, key) => {
          const [StaticCmp, text, ReactCmp = null] = getComponent(block);

          let component;

          if (ReactCmp) {
            component = ReactCmp;
          } else {
            component = (
              <StaticCmp
                key={key}
                id={block.id}
                dangerouslySetInnerHTML={{ __html: text }}
              />
            );
          }

          if (key === displayIndex && bannerElement) {
            if (ReactCmp) {
              displayIndex = +1;
            } else {
              component = (
                <>
                  {component}
                  <div className={styles.bannerContainer}>
                    <Banner {...bannerElement} />
                  </div>
                </>
              );
            }
          }

          return component;
        })}
        <Author {...getUserInfo(author)} />
        <Tags items={tags} />
        <div className={styles.centered}>
          <SocialNetworks />
        </div>
      </div>
      <div className={styles.sidebar} />
    </div>
  );
}
